import { MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { createAction, props } from '@ngrx/store';
import {
  Address,
  Amenity,
  CardType,
  CustomerServiceNote,
  CustomerServiceNoteDetail,
  Driver,
  FavoriteLocation,
  Itinerary,
  Location,
  MobilityProfileDetail,
  Organization,
  OrganizationPaymentMethod,
  OrganizationRider,
  OrganizationRiderDetail,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  Payment,
  PaymentAccount,
  Provider,
  ReservationChargeDetail,
  RideMetadataField,
  Rider,
  RiderCreationRequest,
  RideRequest,
  RideRequestType,
  RiderProfileDetail,
  PersonSuspension,
  PersonSuspensionDetail,
  RideSubscriptionDetail,
  Transaction,
  Trip,
  VehicleType,
  OrganizationRiderPool
} from '@uzurv/clara';
import { ApplicationException, HttpException } from '@uzurv/core';
import { RideLimitDetail } from 'projects/sdk/packages/clara/src/models/ride-limit-detail';
import { OrganizationRiderPaymentAccountDialog } from '../../components/dialogs/payment-account-dialog/organization-rider/organization-rider-payment-account-dialog.dialog';
import { EditRiderDialogComponent } from '../../components/dialogs/edit-rider-dialog';
import { NewPaymentDialogComponent } from '../../components/dialogs/new-payment-dialog/new-payment-dialog.component';
import { RefundPaymentDialogComponent } from '../../components/dialogs/refund-payment-dialog/refund-payment-dialog.component';
import { RiderDetailTabs } from '../../pages/organization-rider-detail/organization-rider-detail.page';
import { OrganizationRiderState } from './organization-rider.reducer';
import { Tab } from '../../services/tab/tab.service';

export const RestoreState = createAction('[Organization Rider] Restore State', props<{ state: OrganizationRiderState }>());

export const ClearState = createAction('[Organization Rider] Clear State');

export const Start = createAction('[Organization Rider] Start', props<Tab<OrganizationRiderState>>());

export const Close = createAction('[Organization Rider] Close', props<{ payload?: { done: boolean } }>());

export const Resume = createAction('[Organization Rider] Resume');

export const GetVehicleTypes = createAction('[Organization Rider] Get vehicle types');

export const GetVehicleTypesSuccess = createAction(
  '[Organization Rider] Get vehicle types success',
  props<{ vehicleTypes: Array<VehicleType> }>()
);

export const GetVehicleTypesFail = createAction('[Organization Rider] Get vehicle types failed', props<{ error: HttpException }>());

export const GetRideRequestTypes = createAction('[Organization Rider] Get ride request types');

export const GetRideRequestTypesSuccess = createAction(
  '[Organization Rider] Get ride request types success',
  props<{ rideRequestTypes: Array<RideRequestType> }>()
);

export const GetRideRequestTypesFail = createAction(
  '[Organization Rider] Get ride request types failed',
  props<{ error: HttpException }>()
);

export const GetAmenities = createAction('[Organization Rider] Get amenities');

export const GetAmenitiesSuccess = createAction('[Organization Rider] Get amenities success', props<{ amenities: Array<Amenity> }>());

export const GetAmenitiesFail = createAction('[Organization Rider] Get amenities failed', props<{ error: HttpException }>());

export const SelectAmenities = createAction('[Organization Rider] Select amenities', props<{ amenities: Array<Amenity> }>());

export const GetRiderPaymentAccountsSuccess = createAction(
  '[Organization Rider] Get organization rider payment accounts success',
  props<{ paymentAccounts: Array<PaymentAccount> }>()
);

export const GetRiderPaymentAccountsFail = createAction(
  '[Organization Rider] Get organization rider payment accounts failed',
  props<{ error: HttpException }>()
);

export const GetRiderFavoriteLocations = createAction(
  '[Organization Rider] Get organization rider favorite locations',
  props<{ payload: { organizationRiderId: number } }>()
);

export const GetRiderFavoriteLocationsSuccess = createAction(
  '[Organization Rider] Get organization rider favorite locations success',
  props<{ favoriteLocations: Array<FavoriteLocation> }>()
);

export const GetRiderFavoriteLocationsFail = createAction(
  '[Organization Rider] Get organization rider favorite locations failed',
  props<{ error: HttpException }>()
);

export const GetOrganizationRiderServiceLevelsSuccess = createAction(
  '[Organization Rider] Get organization rider service levels success',
  props<{ organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel> }>()
);

export const GetOrganizationRiderServiceLevelsFail = createAction(
  '[Organization Rider] Get organization rider service levels failed',
  props<{ error: HttpException }>()
);

export const GetOrganizationRiderAddressSuccess = createAction(
  '[Organization Rider] Get organization rider address success',
  props<{ address: Address }>()
);

export const GetOrganizationRiderAddressFail = createAction(
  '[Organization Rider] Get organization rider address failed',
  props<{ error: HttpException }>()
);

export const GetRideLimits = createAction('[Organization Rider] Get ride limits');

export const GetRideLimitsSuccess = createAction(
  '[Organization Rider] Get ride limits success',
  props<{ rideLimitDetail: RideLimitDetail }>()
);

export const GetRideLimitsFail = createAction('[Organization Rider] Get ride limits failed', props<{ error: HttpException }>());

export const GetRideMetadataFields = createAction(
  '[Organization Rider] Get ride metadata fields',
  props<{ payload: { organizationId: number; transitProgramId: number } }>()
);

export const GetRideMetadataFieldsSuccess = createAction(
  '[Organization Rider] Get ride metadata fields success',
  props<{ rideMetadataFields: Array<RideMetadataField> }>()
);

export const GetRideMetadataFieldsFail = createAction(
  '[Organization Rider] Get ride metadata fields failed',
  props<{ error: HttpException }>()
);

export const GetOrganizationServiceLevelsSuccess = createAction(
  '[Organization Rider] Get organization service levels success',
  props<{ organizationServiceLevels: Array<OrganizationServiceLevel> }>()
);

export const GetOrganizationServiceLevelsFail = createAction(
  '[Organization Rider] Get organization service levels failed',
  props<{ error: HttpException }>()
);

export const SearchRider = createAction('[Organization Rider] Search organization rider', props<{ searchString: string }>());

export const RiderSearchSuccess = createAction(
  '[Organization Rider] Organization Rider search success',
  props<{ organizationRiderListItems: Array<OrganizationRiderListItem> }>()
);

export const RiderSearchFail = createAction('[Organization Rider] Organization Rider search failed', props<{ error: HttpException }>());

export const SearchRiderNewItinerary = createAction(
  '[Organization Rider] Search organization rider new itinerary',
  props<{ searchString: string }>()
);

export const SearchRiderNewItinerarySuccess = createAction(
  '[Organization Rider] Search rider new itinerary success',
  props<{ organizationRiderListItems: Array<OrganizationRiderListItem> }>()
);

export const SearchRiderNewItineraryFail = createAction(
  '[Organization Rider] Search organization rider new itinerary fail',
  props<{ error: HttpException }>()
);

export const AddNewRider = createAction('[Organization Rider] Add new organization rider', props<{ payload: boolean }>());

export const ConfirmCancelAddNewRider = createAction(
  '[Organization Rider] Confirm cancel add new organization rider',
  // need to know what the page is to know what to do, will refactor in future itinerary ticket
  props<{ payload: { forNewItinerary: boolean } }>()
);

export const CancelAddNewRider = createAction(
  '[Organization Rider] Cancel add new organization rider',
  // need to know what the page is to know what to do, will refactor in future itinerary ticket
  props<{ payload: { forNewItinerary: boolean } }>()
);

export const CreateRiderWithProfilesAndServiceLevels = createAction(
  '[Organization Rider] Create organization rider with profiles',
  props<{ riderCreationRequest: RiderCreationRequest }>()
);

export const CreateRiderWithProfilesAndServiceLevelsSuccess = createAction(
  '[Organization Rider] Create organization rider with profiles success',
  props<{ rider: Rider }>()
);

export const CreateRiderWithProfilesAndServiceLevelsFail = createAction(
  '[Organization Rider] Create organization rider with profiles fail',
  props<{ error: HttpException }>()
);

export const UpdateRiderMobilityProfile = createAction(
  '[Organization Rider] Update rider mobility profile',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const UpdateRiderMobilityProfileSuccess = createAction(
  '[Organization Rider] Update rider mobility profile success',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const UpdateRiderMobilityProfileFail = createAction(
  '[Organization Rider] Update rider mobility profile fail',
  props<{ error: HttpException }>()
);

export const VerifyRiderMobilityProfile = createAction(
  '[Organization Rider] Verify rider mobility profile',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const VerifyRiderMobilityProfileSuccess = createAction(
  '[Organization Rider] Verify rider mobility profile success',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const VerifyRiderMobilityProfileFail = createAction(
  '[Organization Rider] Verify rider mobility profile fail',
  props<{ error: HttpException }>()
);

export const CreateOrganizationRider = createAction(
  '[Organization Rider] Create organization rider',
  // need to know what the page is to know what to do, will refactor in future itinerary ticket
  props<{
    payload: {
      organizationRider: OrganizationRider;
      organization: Organization;
      organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>;
      forNewItinerary: boolean;
      forAddingOrganizationRider?: boolean;
    };
  }>()
);

export const CreateOrganizationRiderSuccess = createAction(
  '[Organization Rider] Create organization rider success',
  props<{
    payload: {
      organizationRiderId: number;
      organizationId: number;
      organizationRiderDetail: OrganizationRiderDetail;
      organization: Organization;
      forNewItinerary: boolean;
      forAddingOrganizationRider?: boolean;
    };
  }>()
);

export const CreateOrganizationRiderFail = createAction(
  '[Organization Rider] Create organization rider fail',
  props<{ error: HttpException }>()
);

export const ClearRiderSearch = createAction('[Organization Rider] Clear organization rider search');

export const SelectRider = createAction(
  '[Organization Rider] Select organization rider',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const GetOrganizationRiderDetail = createAction(
  '[Organization Rider] Get organization rider detail',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const GetOrganizationRiderDetailSuccess = createAction(
  '[Organization Rider] Get organization rider detail success',
  props<{ payload: OrganizationRiderDetail }>()
);

export const GetOrganizationRiderDetailFail = createAction(
  '[Organization Rider] Get organization rider detail fail',
  props<{ error: HttpException }>()
);

export const GetOrganizationSuccess = createAction(
  '[Organization Rider] Get organization success',
  props<{ organization: Organization }>()
);

export const GetOrganizationFail = createAction('[Organization Rider] Get organization failed', props<{ error: HttpException }>());

export const GetOrganizations = createAction('[Organization Rider] Get organizations');

export const GetOrganizationsSuccess = createAction(
  '[Organization Rider] Get organizations success',
  props<{ organizations: Array<Organization> }>()
);

export const GetOrganizationsFail = createAction('[Organization Rider] Get organizations fail', props<{ error: HttpException }>());

export const SetIsRideSubscription = createAction(
  '[Organization Rider] Select ride subscription',
  props<{ isRideSubscription: boolean }>()
);

export const SelectDaysOfTheWeek = createAction('[Organization Rider] Select days of the week', props<{ daysOfTheWeek: Array<string> }>());

export const SelectSubscriptionStartDate = createAction(
  '[Organization Rider] Select subscription start date',
  props<{ subscriptionStartDate: Date }>()
);

export const SelectSubscriptionEndDate = createAction(
  '[Organization Rider] Select subscription end date',
  props<{ subscriptionEndDate: Date }>()
);

export const SelectDate = createAction('[Organization Rider] Select date', props<{ date: Date }>());

export const SetPartnerIdentifier = createAction('[Organization Rider] Set partner identifier', props<{ payload: string }>());

export const SelectVehicleType = createAction('[Organization Rider] Select vehicle type', props<{ vehicleType: VehicleType }>());

// Pass isReturnLeg = true for a return leg
export const NewLeg = createAction('[Organization Rider] New leg', props<{ payload?: { isReturnLeg: boolean } }>());

// Pass isLast = true for the last leg in the itinerary,
// and if there is a return leg, that must be it, so set state
// returnLegCreated to false

export const RemoveLeg = createAction('[Organization Rider] Remove leg', props<{ payload: { index: number; isLast?: boolean } }>());

export const UpdateLeg = createAction('[Organization Rider] Update leg', props<{ payload: { index: number; leg: RideRequest } }>());

export const ResetAllLegs = createAction('[Organization Rider] Reset all legs');

export const ReplaceFavoriteLocation = createAction(
  '[Organization Rider] Replace favorite location',
  props<{ payload: { toDelete: FavoriteLocation; toCreate: FavoriteLocation } }>()
);

export const AddFavoriteLocation = createAction(
  '[Organization Rider] Add favorite location',
  props<{ payload: { location: Location; label: string } }>()
);

export const AddFavoriteLocationSuccess = createAction(
  '[Organization Rider] Add favorite location success',
  props<{ favoriteLocation: FavoriteLocation }>()
);

export const AddFavoriteLocationFail = createAction(
  '[Organization Rider] Add favorite location fail',
  props<{ payload: { location: Location; label: string; error: HttpException } }>()
);

export const DeleteFavoriteLocation = createAction(
  '[Organization Rider] Delete favorite location',
  props<{ favoriteLocation: FavoriteLocation }>()
);

export const DeleteFavoriteLocationSuccess = createAction(
  '[Organization Rider] Delete favorite location success',
  props<{ favoriteLocation: FavoriteLocation }>()
);

export const DeleteFavoriteLocationFail = createAction(
  '[Organization Rider] Delete favorite location fail',
  props<{ error: HttpException }>()
);

export const EnableOrganizationRiderServiceLevel = createAction(
  '[Organization Rider] Enable organization rider service level',
  props<{ organizationRiderServiceLevel: OrganizationRiderServiceLevel }>()
);

export const EnableOrganizationRiderServiceLevelSuccess = createAction(
  '[Organization Rider] Enable organization rider service level success',
  props<{ organizationRiderServiceLevel: OrganizationRiderServiceLevel }>()
);

export const EnableOrganizationRiderServiceLevelFail = createAction(
  '[Organization Rider] Enable organization rider service level fail',
  props<{ error: HttpException }>()
);

export const DisableOrganizationRiderServiceLevel = createAction(
  '[Organization Rider] Disable organization rider service level',
  props<{ organizationRiderServiceLevel: OrganizationRiderServiceLevel }>()
);

export const DisableOrganizationRiderServiceLevelSuccess = createAction(
  '[Organization Rider] Disable organization rider service level success',
  props<{ organizationRiderServiceLevel: OrganizationRiderServiceLevel }>()
);

export const DisableOrganizationRiderServiceLevelFail = createAction(
  '[Organization Rider] Disable organization rider service level fail',
  props<{ error: HttpException }>()
);

export const SelectOrganizationPaymentMethod = createAction(
  '[Organization Rider] Select organization payment method',
  props<{ organizationPaymentMethod: OrganizationPaymentMethod }>()
);

export const SelectPaymentAccount = createAction(
  '[Organization Rider] Select payment account',
  props<{ paymentAccount: PaymentAccount }>()
);

export const AddPaymentAccount = createAction('[Organization Rider] Add payment account');

export const AddPaymentAccountSuccess = createAction(
  '[Organization Rider] Add payment account success',
  props<{ paymentAccount: PaymentAccount }>()
);

export const CancelAddPaymentAccount = createAction('[Organization Rider] Cancel add payment account');

export const ItineraryValidated = createAction('[Organization Rider] Itinerary validated');

export const ItineraryInvalid = createAction('[Organization Rider] Itinerary invalid', props<{ payload: Array<string> }>());

export const SubmitItinerary = createAction('[Organization Rider] Submit itinerary');

export const SubmitRideSubscription = createAction('[Organization Rider] Submit ride subscription');

export const ConfirmSubmission = createAction('[Organization Rider] Confirm submission');

export const ConfirmRideSubscriptionSubmission = createAction('[Organization Rider] Confirm ride subscription submission');

export const AbandonSubmission = createAction('[Organization Rider] Abandon submission');

export const SubmitItinerarySuccess = createAction('[Organization Rider] Submit itinerary success', props<{ itinerary: Itinerary }>());

export const SubmitItineraryFail = createAction('[Organization Rider] Submit itinerary fail', props<{ error: HttpException }>());

export const SubmitRideSubscriptionSuccess = createAction(
  '[Organization Rider] Submit ride subscription success',
  props<{ rideSubscriptionDetail: RideSubscriptionDetail }>()
);

export const SubmitRideSubscriptionFail = createAction(
  '[Organization Rider] Submit ride subscription fail',
  props<{ error: HttpException }>()
);

export const OpenOrganizationRiderDetail = createAction(
  '[Organization Rider] Open organization rider detail',
  props<{ organizationRider: OrganizationRiderDetail | OrganizationRiderListItem; subTab?: RiderDetailTabs }>()
);

export const ShowMoreResults = createAction('[Organization Rider] Show more results');

export const OpenAddOrganizationRiderDialog = createAction('[Organization Rider] Open add organization rider dialog');

export const OpenEditUzurvRiderDetail = createAction('[Organization Rider] Open edit uzurv rider detail');

export const OpenEditOrganizationRiderDetail = createAction(
  '[Organization Rider] Open edit organization rider detail',
  props<{
    payload: {
      organizationRiderDetail: OrganizationRiderDetail;
      organizationServiceLevels: Array<OrganizationServiceLevel>;
      organizationRiderPools: Array<OrganizationRiderPool> | null;
    };
  }>()
);

export const ModifyOrganizationRiderDetail = createAction(
  '[Organization Rider] Modify organization rider details',
  props<{
    payload: {
      organizationRiderDetail: OrganizationRiderDetail;
      organizationRiderServiceLevels?: Array<OrganizationRiderServiceLevel>;
      organizationRiderPool?: OrganizationRiderPool;
    };
  }>()
);

export const ModifyOrganizationRiderDetailSuccess = createAction(
  '[Organization Rider] Modify organization rider details success',
  props<{ organizationRiderDetail: OrganizationRiderDetail }>()
);

export const ModifyOrganizationRiderDetailFail = createAction(
  '[Organization Rider] Modify organization rider details fail',
  props<{ error: HttpException }>()
);

export const UpdateUzurvRider = createAction(
  '[Organization Rider] Update uzurv rider',
  props<{ payload: { rider: Rider; address?: Address } }>()
);

export const UpdateUzurvRiderSuccess = createAction(
  '[Organization Rider] Update uzurv rider success',
  props<{ payload: { rider: Rider; organizationRiderAddress?: Address } }>()
);

export const UpdateUzurvRiderFail = createAction('[Organization Rider] Update uzurv rider fail', props<{ error: HttpException }>());

export const UpdateOrganizationRider = createAction(
  '[Organization Rider] Update organization rider',
  props<{
    payload: {
      organization: Organization;
      organizationRider: OrganizationRiderDetail;
      dialogRef?: MatDialogRef<EditRiderDialogComponent>;
      address?: Address;
    };
  }>()
);

export const UpdateOrganizationRiderSuccess = createAction(
  '[Organization Rider] Update organization rider success',
  props<{ payload: { organizationRiderId: number; organizationId: number; address?: Address } }>()
);

export const UpdateOrganizationRiderFail = createAction(
  '[Organization Rider] Update organization rider fail',
  props<{ error: HttpException }>()
);

export const UpdateOrganizationRiderEligibility = createAction(
  '[Organization Rider] Update organization rider eligibility',
  props<{ organizationRiderDetail: OrganizationRiderDetail; isRiderEligible: boolean }>()
);

export const UpdateOrganizationRiderEligibilitySuccess = createAction(
  '[Organization Rider] Update organization rider eligibility success',
  props<{ payload: { organizationRiderDetail: OrganizationRiderDetail; isRiderEligible: boolean } }>()
);

export const UpdateOrganizationRiderEligibilityFail = createAction(
  '[Organization Rider] Update organization rider eligibility fail',
  props<{ error: HttpException }>()
);

export const GetRiderProfileSuccess = createAction(
  '[Organization Rider] Get rider profile success',
  props<{ riderProfile: RiderProfileDetail }>()
);

export const GetRiderProfileFail = createAction('[Organization Rider] Get rider profile fail', props<{ error: HttpException }>());

export const DeclineRiderEmail = createAction(
  '[Organization Rider] Decline organization rider email',
  props<{ organizationRiderDetail: OrganizationRiderDetail }>()
);

export const RemoveFavoriteDriver = createAction('[Organization Rider] Remove favorited driver', props<{ driver: Driver }>());

export const RemoveFavoriteDriverSuccess = createAction(
  '[Organization Rider] Remove favorited driver success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const RemoveFavoriteDriverFail = createAction(
  '[Organization Rider] Remove favorited driver fail',
  props<{ error: HttpException }>()
);

export const RemoveIgnoredDriver = createAction('[Organization Rider] Remove ignored driver', props<{ driver: Driver }>());

export const RemoveIgnoredDriverSuccess = createAction(
  '[Organization Rider] Remove ignored driver success',
  props<{ payload: { organizationRiderId: number; organizationId: number; driver: Driver } }>()
);

export const RemoveIgnoredDriverFail = createAction('[Organization Rider] Remove ignored driver fail', props<{ error: HttpException }>());

export const GetPaymentMethods = createAction('[Organization Rider] Get payment methods');

export const GetPaymentMethodsSuccess = createAction(
  '[Organization Rider] Get payment methods success',
  props<{ organizationPaymentMethods: Array<OrganizationPaymentMethod> }>()
);

export const GetPaymentMethodsFail = createAction('[Organization Rider] Get payment methods fail', props<{ error: HttpException }>());

export const GetCardTypes = createAction('[Organization Rider] Get card types');

export const GetCardTypesSuccess = createAction('[Organization Rider] Get card types success', props<{ cardTypes: Array<CardType> }>());

export const GetCardTypesFail = createAction('[Organization Rider] Get card types fail', props<{ error: HttpException }>());

export const CreatePaymentAccount = createAction(
  '[Organization Rider] Create payment account',
  props<{ payload: { dialogRef: MatDialogRef<OrganizationRiderPaymentAccountDialog>; paymentAccount: PaymentAccount } }>()
);

export const CreatePaymentAccountSuccess = createAction(
  '[Organization Rider] Create payment account success',
  props<{ paymentAccount: PaymentAccount }>()
);

export const CreatePaymentAccountFail = createAction(
  '[Organization Rider] Create payment account fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const EditPaymentAccount = createAction(
  '[Organization Rider] Edit payment account',
  props<{ payload: { dialogRef: MatDialogRef<OrganizationRiderPaymentAccountDialog>; paymentAccount: PaymentAccount } }>()
);

export const EditPaymentAccountSuccess = createAction(
  '[Organization Rider] Edit payment account success',
  props<{ paymentAccount: PaymentAccount }>()
);

export const EditPaymentAccountFail = createAction(
  '[Organization Rider] Edit payment account fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const DeletePaymentAccount = createAction(
  '[Organization Rider] Delete payment account',
  props<{ payload: { dialogRef: MatDialogRef<OrganizationRiderPaymentAccountDialog>; paymentAccount: PaymentAccount } }>()
);

export const DeletePaymentAccountSuccess = createAction('[Organization Rider] Delete payment account success');

export const DeletePaymentAccountFail = createAction(
  '[Organization Rider] Delete payment account fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetOutstandingCharges = createAction('[Organization Rider] Get outstanding charges');

export const GetOutstandingChargesSuccess = createAction(
  '[Organization Rider] Get outstanding charges success',
  props<{ outstandingCharges: Array<ReservationChargeDetail> }>()
);

export const GetOutstandingChargesFail = createAction(
  '[Organization Rider] Get outstanding charges fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const SetNewItineraryStarted = createAction('[Organization Rider] Set new itinerary started', props<{ isNewItinerary: boolean }>());

export const SubmitPaymentForOutstandingCharges = createAction(
  '[Organization Rider] Submit payment for outstanding charges',
  props<{ payload: { selectedPaymentAccount: PaymentAccount; selectedCharges: Array<ReservationChargeDetail> } }>()
);

export const SubmitPaymentForOutstandingChargesSuccess = createAction(
  '[Organization Rider] Submit payment for outstanding charges success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const SubmitPaymentForOutstandingChargesFail = createAction(
  '[Organization Rider] Submit payment for outstanding charges fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetPaymentHistory = createAction('[Organization Rider] Get payment history');

export const GetPaymentsSuccess = createAction('[Organization Rider] Get payments success', props<{ payments: Array<Payment> }>());

export const GetPaymentsFail = createAction(
  '[Organization Rider] Get payments fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const PagePaymentsList = createAction('[Organization Rider] Page payments list', props<{ pageEvent: PageEvent }>());

export const OpenNewPaymentDialog = createAction('[Organization Rider] Open new payment dialog');

export const CreatePayment = createAction(
  '[Organization Rider] Create payment',
  props<{ payload: { payment: Payment; dialogRef: MatDialogRef<NewPaymentDialogComponent> } }>()
);

export const CreatePaymentSuccess = createAction(
  '[Organization Rider] Create payment success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const CreatePaymentFail = createAction(
  '[Organization Rider] Create payment fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const VoidPayment = createAction('[Organization Rider] Void payment', props<{ payment: Payment }>());

export const VoidPaymentSuccess = createAction(
  '[Organization Rider] Void payment success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const VoidPaymentFail = createAction(
  '[Organization Rider] Void payment fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const ExpandPayment = createAction('[Organization Rider] Expand payment', props<{ payment: Payment }>());

export const GetExpandedPaymentTransactions = createAction('[Organization Rider] Get expanded payment transactions');

export const GetExpandedPaymentTransactionsSuccess = createAction(
  '[Organization Rider] Get expanded payment transactions success',
  props<{ transactions: Array<Transaction> }>()
);

export const GetExpandedPaymentTransactionsFail = createAction(
  '[Organization Rider] Get expanded payment transactions fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const ResubmitPayment = createAction('[Organization Rider] Resubmit payment', props<{ payment: Payment }>());

export const ResubmitPaymentSuccess = createAction(
  '[Organization Rider] Resubmit payment success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const ResubmitPaymentFail = createAction(
  '[Organization Rider] Resubmit payment fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const OpenRefundDialog = createAction('[Organization Rider] Open refund dialog', props<{ payment: Payment }>());

export const RefundPayment = createAction(
  '[Organization Rider] Refund payment',
  props<{ payload: { payment: Payment; dialogRef: MatDialogRef<RefundPaymentDialogComponent> } }>()
);

export const RefundPaymentSuccess = createAction(
  '[Organization Rider] Refund payment success',
  props<{ payload: { organizationRiderId: number; organizationId: number } }>()
);

export const RefundPaymentFail = createAction(
  '[Organization Rider] Refund payment fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetNotesSuccess = createAction(
  '[Organization Rider] Get notes success',
  props<{ payload: Array<CustomerServiceNoteDetail> }>()
);

export const GetNotesFail = createAction('[Organization Rider] Get notes fail', props<{ error: HttpException | ApplicationException }>());

export const AddNote = createAction('[Organization Rider] Add note', props<{ payload: CustomerServiceNote }>());

export const AddNoteSuccess = createAction('[Organization Rider] Add note success', props<{ payload: CustomerServiceNote }>());

export const AddNoteFail = createAction('[Organization Rider] Add note fail', props<{ error: HttpException | ApplicationException }>());

export const GetRiderTrips = createAction('[Organization Rider] Get rider trips');

export const GetRiderTripsSuccess = createAction('[Organization Rider] Get rider trips success', props<{ trips: Array<Trip> }>());

export const GetRiderTripsFail = createAction('[Organization Rider] Get rider trips fail', props<{ error: HttpException }>());

export const TripsPagination = createAction('[Organization Rider] Trips Pagination', props<{ pageEvent: PageEvent }>());

export const SelectRiderDetailTab = createAction(
  '[Organization Rider] Select rider detail tab',
  props<{ selectedRiderDetailTab: RiderDetailTabs }>()
);

export const ConfirmSendUzurvRideAppInvitation = createAction(
  '[Organization Rider] Confirm send uzurv ride app invitation',
  props<{ organizationRiderDetail: OrganizationRiderDetail; fromNewItinerary: boolean }>()
);

export const SendUzurvRideAppInvitation = createAction(
  '[Organization Rider] Send uzurv ride app invitation',
  props<{ organizationRiderDetail: OrganizationRiderDetail; fromNewItinerary: boolean }>()
);

export const SendUzurvRideAppInvitationSuccess = createAction(
  '[Organization Rider] Send uzurv ride app invitation success',
  props<{ rider: OrganizationRiderDetail | Rider }>()
);

export const SendUzurvRideAppInvitationFail = createAction(
  '[Organization Rider] Send uzurv ride app invitation fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const AbandonSendUzurvRideAppInvitation = createAction('[Organization Rider] Abandon send uzurv ride app invitation');

export const FetchAvailableProviders = createAction(
  '[Organization Rider] Fetch available providers',
  props<{ transitProgramId: number; serviceLevelCode: string }>()
);

export const FetchAvailableProvidersSuccess = createAction(
  '[Organization Rider] Fetch available providers success',
  props<{ availableProviders: Array<Provider> }>()
);

export const FetchAvailableProvidersFail = createAction(
  '[Organization Rider] Fetch available providers fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const SelectPreferredProvider = createAction(
  '[Organization Rider] Select preferred provider',
  props<{ preferredProviderId: number }>()
);

export const ShouldShowPreferredProviderSelection = createAction(
  '[Organization Rider] Should show preferred provider selection',
  props<{ shouldShowPreferredProviderSelection: boolean }>()
);

export const ShouldShowPaymentSelection = createAction(
  '[Organization Rider] Should show payment selection',
  props<{ shouldShowPaymentSelection: boolean }>()
);

export const GetListOfOrganizationRiderDetailsSuccess = createAction(
  '[Organization Rider] Get list of organization rider details success',
  props<{ organizationRiderDetails: Array<OrganizationRiderDetail> }>()
);

export const SelectRiderPhoneNumber = createAction(
  '[Organization Rider] Select Rider Phone Number',
  props<{ selectedRiderPhoneNumber: string }>()
);

export const ShouldShowMobilityProfile = createAction(
  '[Organization Rider] Should show mobility profile',
  props<{ shouldShowMobilityProfile: boolean; shouldDisableMobilityProfileSelection: boolean }>()
);

// Rider Suspensions

export const GetRiderSuspensions = createAction('[Organization Rider] Get rider suspensions', props<{ riderId: number }>());

export const GetRiderSuspensionsSuccess = createAction(
  '[Organization Rider] Get rider suspensions success',
  props<{ riderSuspensions: Array<PersonSuspensionDetail> }>()
);

export const GetRiderSuspensionsFail = createAction(
  '[Organization Rider] Get rider suspensions fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const CreateRiderSuspension = createAction('[Organization Rider] Create rider suspension', props<{ rider: Rider }>());

export const CreateRiderSuspensionCancelled = createAction('[Organization Rider] Create rider suspension cancelled');

export const CreateRiderSuspensionSuccess = createAction(
  '[Organization Rider] Create rider suspension success',
  props<{ riderSuspension: PersonSuspensionDetail }>()
);

export const CreateRiderSuspensionFail = createAction(
  '[Organization Rider] Create rider suspension fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const RemoveRiderSuspension = createAction(
  '[Organization Rider] Remove rider suspension',
  props<{ riderId: number; riderSuspensionId: number }>()
);

export const RemoveRiderSuspensionCancelled = createAction('[Organization Rider] Remove rider suspension cancelled');

export const RemoveRiderSuspensionSuccess = createAction(
  '[Organization Rider] Remove rider suspension success',
  props<{ riderSuspensionId: number }>()
);

export const RemoveRiderSuspensionFail = createAction(
  '[Organization Rider] Remove rider suspension fail',
  props<{ error: HttpException | ApplicationException }>()
);
